import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getIcon } from '../../../utils/IconHandler';
import { getPendingStockCountAdjustments, reviewStockCountAdjustment } from '../../../services/StockCountService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { permissionOptions } from '../../../utils/Enums';
import { formatStockQuantity } from '../../../utils/stockQuantity';

const getPermissionId = (label) => permissionOptions.find((p) => p.label === label)?.id;

const hasApprovePermission = () => {
  try {
    const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]').map(Number);
    return userPermissions.includes(getPermissionId('APPROVE_STOCK_COUNT_ADJUSTMENT'));
  } catch (_) {
    return false;
  }
};

// ion-icon no es cuadrado por default, así que sin un tamaño fijo el hover circular
// de IconButton sale ovalado -- mismo fix que StockCountConfigPage.jsx.
const ROUND_ICON_BUTTON_SX = {
  width: 32,
  height: 32,
  minWidth: 32,
  minHeight: 32,
  maxWidth: 32,
  maxHeight: 32,
  padding: 0,
  borderRadius: '50%',
  '&:hover': { borderRadius: '50%' },
};

const CHEVRON_CELL_SX = { width: 40, px: 1 };

const AdjustmentRow = ({ adjustment, onReview, canApprove }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell sx={CHEVRON_CELL_SX}>
          <IconButton size="small" onClick={() => setOpen((previous) => !previous)} sx={ROUND_ICON_BUTTON_SX}>
            {open ? getIcon('chevronDown') : getIcon('chevronForward')}
          </IconButton>
        </TableCell>
        <TableCell>{adjustment.locationName || `#${adjustment.locationId}`}</TableCell>
        <TableCell>{adjustment.referenceName || `#${adjustment.referenceId}`}</TableCell>
        <TableCell>{adjustment.referenceTypeLabel}</TableCell>
        <TableCell align="right">{formatStockQuantity(adjustment.systemStock)}</TableCell>
        <TableCell align="right">{formatStockQuantity(adjustment.countedStock)}</TableCell>
        <TableCell align="right">
          <Chip
            size="small"
            label={adjustment.difference > 0 ? `+${formatStockQuantity(adjustment.difference)}` : formatStockQuantity(adjustment.difference)}
            color={adjustment.difference > 0 ? 'success' : 'error'}
          />
        </TableCell>
        <TableCell align="center">
          {canApprove && (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <Tooltip title="Aprobar ajuste">
                <IconButton
                  size="small"
                  onClick={() => onReview(adjustment, true)}
                  sx={{ ...ROUND_ICON_BUTTON_SX, color: 'success.main' }}
                >
                  {getIcon('checkmark')}
                </IconButton>
              </Tooltip>
              <Tooltip title="Rechazar ajuste">
                <IconButton
                  size="small"
                  onClick={() => onReview(adjustment, false)}
                  sx={{ ...ROUND_ICON_BUTTON_SX, color: 'error.main' }}
                >
                  {getIcon('close')}
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Intentos de conteo</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Intento</TableCell>
                    <TableCell>Cantidad</TableCell>
                    <TableCell>Contado por</TableCell>
                    <TableCell>Fecha</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adjustment.attempts.map((attempt) => (
                    <TableRow key={attempt.attemptNumber}>
                      <TableCell>{attempt.attemptNumber}</TableCell>
                      <TableCell>{formatStockQuantity(attempt.countedStock)}</TableCell>
                      <TableCell>{attempt.countedByEmail}</TableCell>
                      <TableCell>{new Date(attempt.countedAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ReviewDialog = ({ target, onClose, onConfirm, submitting }) => {
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setNotes('');
    setError('');
  }, [target]);

  if (!target) return null;
  const { adjustment, approve } = target;

  const handleConfirm = () => {
    if (!approve && !notes.trim()) {
      setError('El motivo del rechazo es obligatorio');
      return;
    }
    onConfirm(adjustment.id, approve, notes.trim());
  };

  return (
    <Dialog open={Boolean(target)} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{approve ? 'Aprobar ajuste' : 'Rechazar ajuste'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {adjustment.referenceName || `#${adjustment.referenceId}`} en {adjustment.locationName} — sistema {formatStockQuantity(adjustment.systemStock)}, contado {formatStockQuantity(adjustment.countedStock)}
        </Typography>
        {approve ? (
          <Alert severity="info">Al aprobar, se ajusta el stock del sistema con la diferencia contada.</Alert>
        ) : (
          <TextField
            fullWidth
            label="Motivo del rechazo"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setError(''); }}
            error={Boolean(error)}
            helperText={error}
            required
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={approve ? 'success' : 'error'}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : (approve ? 'Aprobar' : 'Rechazar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// onPendingCountChange es opcional -- lo usa StockCountManagementPage para mostrar un badge
// en el tab con la cantidad de ajustes pendientes, sin tener que pedir la lista dos veces.
const StockCountAdjustmentsPage = ({ onPendingCountChange }) => {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [locationFilter, setLocationFilter] = useState('');
  const canApprove = hasApprovePermission();

  // Solo ofrece sucursales que tienen ajustes pendientes; el filtro se limpia solo si la
  // sucursal elegida ya no tiene ninguno (ej. después de aprobar/rechazar el último).
  const locationOptions = useMemo(() => {
    const byId = new Map();
    adjustments.forEach((a) => byId.set(a.locationId, a.locationName || `#${a.locationId}`));
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [adjustments]);

  const effectiveLocationFilter = locationOptions.some((o) => o.id === locationFilter) ? locationFilter : '';
  const visibleAdjustments = effectiveLocationFilter === ''
    ? adjustments
    : adjustments.filter((a) => a.locationId === effectiveLocationFilter);

  const loadAdjustments = async () => {
    try {
      setLoading(true);
      const data = await getPendingStockCountAdjustments();
      setAdjustments(data || []);
    } catch (error) {
      console.error('Error loading stock count adjustments', error);
      SnackbarUtils.error('No pudimos cargar los ajustes pendientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdjustments();
  }, []);

  useEffect(() => {
    onPendingCountChange?.(adjustments.length);
  }, [adjustments, onPendingCountChange]);

  const handleConfirmReview = async (adjustmentId, approve, notes) => {
    if (!canApprove) return;
    try {
      setSubmitting(true);
      await reviewStockCountAdjustment(adjustmentId, approve, notes);
      SnackbarUtils.success(approve ? 'Ajuste aprobado' : 'Ajuste rechazado');
      setReviewTarget(null);
      await loadAdjustments();
    } catch (error) {
      console.error('Error reviewing stock count adjustment', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos guardar la revisión');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : !adjustments.length ? (
        <Alert severity="success">No hay ajustes pendientes de aprobación.</Alert>
      ) : (
        <>
        {locationOptions.length > 1 && (
          <TextField
            select
            size="small"
            label="Sucursal"
            value={effectiveLocationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            sx={{ mb: 2, minWidth: 240 }}
          >
            <MenuItem value="">Todas las sucursales</MenuItem>
            {locationOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
            ))}
          </TextField>
        )}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={CHEVRON_CELL_SX} />
                <TableCell>Sucursal</TableCell>
                <TableCell>Ítem</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Sistema</TableCell>
                <TableCell align="right">Contado</TableCell>
                <TableCell align="right">Diferencia</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleAdjustments.map((adjustment) => (
                <AdjustmentRow
                  key={adjustment.id}
                  adjustment={adjustment}
                  canApprove={canApprove}
                  onReview={(item, approve) => setReviewTarget({ adjustment: item, approve })}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      <ReviewDialog
        target={reviewTarget}
        onClose={() => (submitting ? null : setReviewTarget(null))}
        onConfirm={handleConfirmReview}
        submitting={submitting}
      />
    </Box>
  );
};

export default StockCountAdjustmentsPage;
